import { MUTATION_KEYS, QUERY_KEYS, LOCIZE_NS } from '@avanta/shared/constants/common';
import { useMutation, type UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { ROUTES } from 'constants/routes';
import { AppointmentService } from 'services/appointments';
import { type CancelAppointmentPayload } from 'types/appointments';

export const useCancelAppointment = (
  appointmentId: string
): UseMutationResult<void, Error, CancelAppointmentPayload> => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  return useMutation<void, Error, CancelAppointmentPayload>({
    mutationKey: [MUTATION_KEYS.CANCEL_APPOINTMENT, appointmentId],
    mutationFn: async (body: CancelAppointmentPayload) => {
      await AppointmentService.cancel(body);
    },
    onSuccess: () => {
      toast.success(t('CancelAppointment.success'));
      const fromAppointments = matchPath({ path: ROUTES.APPOINTMENT_DETAIL, end: true }, pathname);
      navigate(fromAppointments ? ROUTES.APPOINTMENTS : ROUTES.PATIENTS);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FETCH_NEXT_APPOINTMENTS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FETCH_ENTERPRISE_APPOINTMENTS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FETCH_COUPONS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FETCH_PATIENT] });
    },
    onError: () => {
      toast.error(t('CancelAppointment.error'));
    },
  });
};
